import { useEffect, useRef, useState } from "react";

type ToastKind = "online" | "offline";

/** How long the toast stays on screen after a connectivity change. */
const VISIBLE_MS = 5000;

/**
 * A YouTube-style transient connectivity bar. It is silent on initial load and
 * only surfaces when connectivity *changes*: a green "Online" bar when the
 * connection returns, a red "Offline" bar when it drops. Each appears at the
 * bottom of the viewport for {@link VISIBLE_MS} and then auto-dismisses. A
 * second change before the timer fires swaps the message and restarts the
 * countdown.
 *
 * Replaces the always-on header pill, which collided with the ADMIN badge.
 */
export function ConnectivityToast(): JSX.Element | null {
  const [toast, setToast] = useState<ToastKind | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const show = (kind: ToastKind): void => {
      setToast(kind);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setToast(null), VISIBLE_MS);
    };
    const on = (): void => show("online");
    const off = (): void => show("offline");
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  if (toast === null) return null;

  const isOnline = toast === "online";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`vmc-connectivity-toast vmc-connectivity-toast-${toast}`}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 3000,
        padding: "10px 16px",
        textAlign: "center",
        fontFamily: "system-ui",
        fontSize: 14,
        fontWeight: 600,
        color: "#fff",
        backgroundColor: isOnline ? "#1a7f37" : "#c0392b",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.35)",
        animation: "vmc-connectivity-slide-up 0.2s ease-out",
      }}
    >
      {isOnline
        ? "Back online — changes will sync"
        : "Offline — changes will sync when you reconnect"}
    </div>
  );
}
